import { FunctionComponent, PropsWithChildren, useLayoutEffect, useRef, useState } from 'react';
import { useMount } from 'react-use';
import { Connector, useAccount, useConnect, useSignMessage } from 'wagmi';
import { apiBaseUrl, chainId } from '@/lib/globals';
import { useLoading } from '@/hooks/useLoading';

type LeaderboardEntry = {
    code: string;
    nickname: string | null;
    points: number;
};

type State =
    | {
          type: 'CODE';
      }
    | {
          type: 'ADDRESS';
      }
    | {
          type: 'SUCCESS';
          points: number;
      };

const PointsformTable: FunctionComponent<PropsWithChildren<unknown>> = ({ children }) => {
    const ref = useRef<HTMLTableElement>(null);
    useLayoutEffect(() => {
        ref.current?.setAttribute('border', '10');
    }, [ref]);
    return (
        <table className="pointsform" ref={ref}>
            {children}
        </table>
    );
};

export const Points: FunctionComponent = () => {
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [code, setCode] = useState('');
    const [state, setState] = useState<State | null>(null);
    const { connectors, connectAsync } = useConnect();
    const { address } = useAccount();
    const { signMessageAsync } = useSignMessage();
    const walletConnectConnector = connectors.find((connector) => connector.id !== 'injected');
    const injectedConnector = connectors.find((connector) => connector.id === 'injected');

    const mainRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        mainRef.current?.setAttribute('align', 'center');
    }, [mainRef]);

    useLayoutEffect(() => {
        document.body.classList.add('theme-points');
        return () => {
            document.body.classList.remove('theme-points');
        };
    }, []);

    const refreshLeaderboard = async (): Promise<void> => {
        const res = await fetch(`${apiBaseUrl}game/points/leaderboard`);
        if (!res.ok) {
            console.error(`Failed to refresh leaderboard: ${res.status}`);
            return;
        }

        const { leaderboard } = await res.json();
        setLeaderboard(leaderboard);
    };

    useMount(() => {
        void refreshLeaderboard();
    });

    const connect = async (connector?: Connector): Promise<void> => {
        if (connector === undefined) {
            return;
        }
        await connectAsync({ connector, chainId });
    };

    const [submitting, submit] = useLoading(async (address: string) => {
        if (!code) {
            return;
        }

        setState(null);

        const message = `I claim points for all Onlybots owned by this wallet.\n\ncode: ${code}`;
        const signature = await signMessageAsync({ message });

        const res = await fetch(`${apiBaseUrl}game/points/claim-bots`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                address,
                signature,
                code: code.trim(),
            }),
        });

        if (!res.ok) {
            if (res.status === 404) {
                // 404: if text body is CODE, then code is invalid
                // 404: if text body is ADDRESS, then address is invalid
                const text = await res.text();
                if (text === 'CODE') {
                    setState({ type: 'CODE' });
                    return;
                }

                if (text === 'ADDRESS') {
                    setState({ type: 'ADDRESS' });
                    return;
                }
            }

            console.error(`Failed to claim bots: ${res.status}`);
            return;
        }

        const { points } = await res.json();
        setState({ type: 'SUCCESS', points });
        await refreshLeaderboard();
    });

    return (
        <>
            <link rel="stylesheet" href="/assets/css/points-2.css" />
            <div id="story" />

            <div ref={mainRef}>
                <br />
                <br />
                <br />
                <br />
                <br />
                <br />
                <img src="/assets/img/home/pointshero.gif" alt="Get Points" />
                <h2>
                    <span className="blink">Points for bots by bots.</span>
                </h2>
                <br />
                <br />
                <br />
                <br />
                <h4>Earn points by:</h4>
                <p>
                    <b>1. Talking to any Onlybot every day</b> - get a points bonus once per day. With bonuses coming
                    for daily streaks!
                </p>
                <p>
                    <b>2. Sharing your Code</b> from your profile in the{' '}
                    <a href="https://only.bot/app" target="_blank" rel="noreferrer">
                        <em>Onlybots App.</em>
                    </a>{' '}
                    If any of your friends boot up the app and use your Code when registering, you <i>each</i> get
                    points!
                </p>
                <p>
                    <b>3. Collecting Onlybots!</b> If you have or get new Onlybots,{' '}
                    <a href="#signin">
                        <em>Sign in with your wallet</em>
                    </a>{' '}
                    – no transaction needed. If you sell your Onlybots, you can lose those points to the new owner.
                </p>
                <p>
                    <b>4. Watching for announcements…</b> follow{' '}
                    <a href="https://twitter.com/onlydotbot" target="_blank" rel="noreferrer">
                        <em>@onlydotbot</em>
                    </a>
                    !
                </p>
                <br />
                <br />
                <h4>Gain rewards by winning or reaching milestones.</h4>
                <br />
                <br />
                <img alt="rainbow" width="860" height="2" src="/assets/img/home/div-rainbow.webp" />
                <br />
                <br />
                <div id="signin" />
                {state === null && (
                    <PointsformTable>
                        <thead>
                            <tr>
                                <th colSpan={2}>
                                    <h2>Sign In for Points</h2>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <label htmlFor="code">Your Code:</label>
                                    <input
                                        id="code"
                                        name="code"
                                        value={code}
                                        maxLength={6}
                                        onChange={(e) => setCode(e.target.value)}
                                        type="text"
                                        placeholder="code"
                                    />
                                </td>
                                <td>
                                    {address === undefined ? (
                                        <>
                                            <label>Connect With:</label>
                                            <button onClick={() => void connect(walletConnectConnector)}>
                                                WalletConnect
                                            </button>
                                            <button onClick={() => void connect(injectedConnector)}>
                                                MetaMask/Coinbase
                                            </button>
                                        </>
                                    ) : (
                                        <button disabled={submitting} onClick={() => void submit(address)}>
                                            Submit
                                        </button>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </PointsformTable>
                )}
                {state?.type === 'CODE' && (
                    <PointsformTable>
                        <thead>
                            <tr>
                                <th colSpan={2}>
                                    <h2>Sorry :P</h2>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>That code doesn't exist.</td>
                            </tr>
                        </tbody>
                    </PointsformTable>
                )}
                {state?.type === 'ADDRESS' && (
                    <PointsformTable>
                        <thead>
                            <tr>
                                <th colSpan={2}>
                                    <h2>Sorry :P</h2>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>There are no eligible Onlybots in your wallet.</td>
                            </tr>
                        </tbody>
                    </PointsformTable>
                )}
                {state?.type === 'SUCCESS' && (
                    <PointsformTable>
                        <thead>
                            <tr>
                                <th colSpan={2}>
                                    <h2>You got Points!</h2>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <span className="blink">
                                        <strong>Congratulations!</strong>
                                    </span>{' '}
                                    You got {state.points} points for your Onlybots original!
                                </td>
                            </tr>
                        </tbody>
                    </PointsformTable>
                )}
                <br />
                <br />
                <img alt="rainbow" width="860" height="2" src="/assets/img/home/div-rainbow.webp" />
                <br />
                <br />
                <table className="leaderboard">
                    <thead>
                        <tr>
                            <th colSpan={2}>
                                <h2>Top 10 Leaderboard</h2>
                                Check out who's winning
                                <br />
                                <br />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {leaderboard.map((entry, index) => (
                            <tr key={entry.code}>
                                <td>
                                    {index + 1}.{' '}
                                    {entry.nickname ? (
                                        <>
                                            {entry.nickname} <em>({entry.code})</em>
                                        </>
                                    ) : (
                                        entry.code
                                    )}
                                </td>
                                <td align="right">{entry.points}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <br />
                <br />
                <br />
                <br />
                <a href="/">
                    <img src="/assets/img/home/go-back.webp" alt="Go Back" />
                </a>
                <br />
                <br />
                <br />
                <br />
                <br />
                <br />
                <br />
                <br />
                &copy; 199X - 2024. Onlybots is a product of <a href="https://anima.supply">Anima</a> in partnership
                with{' '}
                <a href="https://www.thegoodfreninternationalfoundation.com/">
                    The&nbsp;Goodfren&nbsp;International&nbsp;Foundation.
                </a>
                <br />
                <br />
                <br />
                <br />
            </div>
        </>
    );
};
